import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthIntegrationTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { NotificationService } from '../notifications/notification.service';
import { GrowthAdminService, GrowthConnectorDto } from './growth-admin.service';
import {
  GROWTH_CONNECTOR_DIRECTIONS,
  GROWTH_CONNECTOR_KINDS,
  GROWTH_CONNECTOR_SCHEDULE_MODES,
  GROWTH_CONNECTOR_STATUSES,
  GROWTH_INTEGRATION_BUILTIN_AUTH_CONFIG_REFS,
  emptyConnectorMappingJson,
  growthConnectorMappingFields,
  mergeConnectorMappingJson,
  toNotificationProviderAuthConfigRef
} from './growth-integration-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-integration-connectors',
  imports: [
    AinPageComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    StructuredJsonFormComponent,
    TableModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    DialogModule,
    RouterLink
  ],
  template: `
    <ain-page [title]="t('growth.integration.connectors.title')" [description]="t('growth.integration.connectors.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.integration.connectors.help.title'"
        [descriptionKey]="'growth.integration.connectors.help.description'"
        [bulletKeys]="['growth.integration.connectors.help.bullets.registry', 'growth.integration.connectors.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <input
            pInputText
            type="text"
            [(ngModel)]="searchTerm"
            [placeholder]="t('growth.integration.connectors.search')" />
          <p-select
            [options]="statusOptions()"
            [(ngModel)]="statusFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.integration.connectors.filterStatus')" />
          <p-select
            [options]="directionOptions()"
            [(ngModel)]="directionFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.integration.connectors.filterDirection')" />
          <button pButton type="button" [label]="t('growth.integration.connectors.search')" (click)="applyFilters()"></button>
          <button pButton type="button" [label]="t('growth.integration.connectors.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button
            pButton
            type="button"
            [label]="t('growth.integration.connectors.actions.openSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openSelectedDetail()"></button>
          <button pButton type="button" [label]="t('growth.integration.connectors.actions.new')" (click)="openCreateDialog()"></button>
          <a class="link-btn" routerLink="/growth/integration/sync-status">{{ t('growth.integration.connectors.openSyncStatus') }}</a>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="connectorKey">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.integration.connectors.columns.connectorKey') }}</th>
              <th>{{ t('growth.integration.connectors.columns.displayName') }}</th>
              <th>{{ t('growth.integration.connectors.columns.direction') }}</th>
              <th>{{ t('growth.integration.connectors.columns.kind') }}</th>
              <th>{{ t('growth.integration.connectors.columns.providerKey') }}</th>
              <th>{{ t('growth.integration.connectors.columns.status') }}</th>
              <th>{{ t('growth.integration.connectors.columns.scheduleMode') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.connectorKey)">{{ row.connectorKey }}</button>
              </td>
              <td>{{ row.displayName }}</td>
              <td>{{ enumLabel('direction', row.direction) }}</td>
              <td>{{ enumLabel('kind', row.kind) }}</td>
              <td>{{ row.providerKey }}</td>
              <td>{{ enumLabel('status', row.status) }}</td>
              <td>{{ enumLabel('scheduleMode', row.scheduleMode) }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>

    <p-dialog
      [header]="t('growth.integration.connectors.createDialog.title')"
      [visible]="createDialogVisible()"
      (visibleChange)="createDialogVisible.set($event)"
      [modal]="true">
      <ain-info-hint
        [titleKey]="'growth.integration.connectors.createDialog.help.title'"
        [descriptionKey]="'growth.integration.connectors.createDialog.help.description'" />
      <div class="dialog-grid">
        <ain-form-field [label]="t('growth.integration.connectors.fields.connectorKey')">
          <input pInputText type="text" [(ngModel)]="createModel.connectorKey" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.integration.connectors.fields.displayName')">
          <input pInputText type="text" [(ngModel)]="createModel.displayName" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.integration.connectors.fields.direction')">
          <p-select [options]="directionOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.direction" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.integration.connectors.fields.kind')">
          <p-select [options]="kindOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.kind" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.integration.connectors.fields.providerKey')">
          <input pInputText type="text" [(ngModel)]="createModel.providerKey" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.integration.connectors.fields.authConfigRef')">
          <p-select
            [options]="authConfigOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createModel.authConfigRef"
            [showClear]="true"
            [filter]="true"
            appendTo="body"
            [placeholder]="t('growth.integration.connectors.fields.authConfigRefPlaceholder')" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.integration.connectors.fields.status')">
          <p-select [options]="statusOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.status" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.integration.connectors.fields.scheduleMode')">
          <p-select [options]="scheduleModeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.scheduleMode" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.integration.connectors.fields.webhookSecret')" class="full">
          <input pInputText type="password" [(ngModel)]="createModel.webhookSecret" [placeholder]="t('growth.integration.connectors.fields.webhookSecretPlaceholder')" />
        </ain-form-field>
      </div>
      <section class="mapping-section">
        <h4>{{ t('growth.integration.connectorDetail.sections.mapping') }}</h4>
        <ain-structured-json-form [fields]="mappingFields" [(payloadJson)]="createModel.mappingJson" />
      </section>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="createDialogVisible.set(false)"></button>
        <button pButton type="button" [label]="t('common.save')" [loading]="savingCreate()" (click)="saveCreate()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 10px; }
    .dialog-grid .full { grid-column: 1 / -1; }
    .mapping-section { margin-top: 12px; display: grid; gap: 8px; }
    .mapping-section h4 { margin: 0; font-size: 14px; }
    @media (max-width: 640px) { .dialog-grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthIntegrationConnectorsComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly notificationApi = inject(NotificationService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;
  readonly mappingFields = growthConnectorMappingFields;

  readonly rows = signal<GrowthConnectorDto[]>([]);
  readonly filteredRows = signal<GrowthConnectorDto[]>([]);
  readonly selected = signal<GrowthConnectorDto | null>(null);
  readonly errorMessage = signal('');
  readonly createDialogVisible = signal(false);
  readonly savingCreate = signal(false);
  readonly authConfigOptions = signal<Array<{ label: string; value: string }>>([]);

  searchTerm = '';
  statusFilter: string | null = null;
  directionFilter: string | null = null;

  createModel = {
    connectorKey: '',
    displayName: '',
    direction: 'Inbound' as string,
    kind: 'External' as string,
    providerKey: '',
    authConfigRef: null as string | null,
    status: 'Draft' as string,
    scheduleMode: 'OnDemand' as string,
    webhookSecret: '',
    mappingJson: emptyConnectorMappingJson()
  };

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthIntegrationChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.loadAuthConfigOptions();
    this.load();
  }

  private loadAuthConfigOptions(): void {
    const builtin = GROWTH_INTEGRATION_BUILTIN_AUTH_CONFIG_REFS.map(value => ({
      value,
      label: this.t(`growth.integration.authConfig.${value}`)
    }));
    this.authConfigOptions.set(builtin);

    this.notificationApi.listProviderConfigs().subscribe({
      next: configs => {
        const providerOptions = configs
          .filter(item => item.isEnabled)
          .map(item => ({
            value: toNotificationProviderAuthConfigRef(item.id),
            label: `${item.displayName} (${item.providerKey}/${item.channel})`
          }));
        this.authConfigOptions.set([...builtin, ...providerOptions]);
      },
      error: () => {
        this.authConfigOptions.set(builtin);
      }
    });
  }

  directionOptions(): Array<{ label: string; value: string }> {
    return GROWTH_CONNECTOR_DIRECTIONS.map(value => ({ value, label: this.enumLabel('direction', value) }));
  }

  kindOptions(): Array<{ label: string; value: string }> {
    return GROWTH_CONNECTOR_KINDS.map(value => ({ value, label: this.enumLabel('kind', value) }));
  }

  statusOptions(): Array<{ label: string; value: string }> {
    return GROWTH_CONNECTOR_STATUSES.map(value => ({ value, label: this.enumLabel('status', value) }));
  }

  scheduleModeOptions(): Array<{ label: string; value: string }> {
    return GROWTH_CONNECTOR_SCHEDULE_MODES.map(value => ({ value, label: this.enumLabel('scheduleMode', value) }));
  }

  enumLabel(group: 'direction' | 'kind' | 'status' | 'scheduleMode', value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    return this.t(`growth.integration.enums.${group}.${value}`);
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listGrowthConnectors().subscribe({
      next: data => {
        this.rows.set(data);
        this.applyFilters();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.integration.connectors.messages.loadFailed'));
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    let filtered = this.rows();
    if (this.statusFilter) {
      filtered = filtered.filter(item => item.status === this.statusFilter);
    }
    if (this.directionFilter) {
      filtered = filtered.filter(item => item.direction === this.directionFilter);
    }
    if (term) {
      filtered = filtered.filter(item =>
        item.connectorKey.toLowerCase().includes(term)
        || item.displayName.toLowerCase().includes(term)
        || item.providerKey.toLowerCase().includes(term));
    }
    this.filteredRows.set(filtered);
    const currentKey = this.selected()?.connectorKey;
    this.selected.set(filtered.find(item => item.connectorKey === currentKey) ?? filtered[0] ?? null);
  }

  onSelect(row: GrowthConnectorDto | null): void {
    this.selected.set(row);
  }

  openSelectedDetail(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.connectorKey);
  }

  openDetail(connectorKey: string): void {
    void this.router.navigate(['/growth/integration/connectors', connectorKey]);
  }

  openCreateDialog(): void {
    this.loadAuthConfigOptions();
    this.createModel = {
      connectorKey: '',
      displayName: '',
      direction: 'Inbound',
      kind: 'External',
      providerKey: '',
      authConfigRef: null,
      status: 'Draft',
      scheduleMode: 'OnDemand',
      webhookSecret: '',
      mappingJson: emptyConnectorMappingJson()
    };
    this.createDialogVisible.set(true);
  }

  saveCreate(): void {
    const key = this.createModel.connectorKey.trim();
    if (!key || !this.createModel.displayName.trim() || !this.createModel.providerKey.trim()) {
      this.errorMessage.set(this.t('growth.integration.connectors.messages.validationFailed'));
      return;
    }
    this.savingCreate.set(true);
    this.errorMessage.set('');
    this.api.upsertGrowthConnector({
      connectorKey: key,
      displayName: this.createModel.displayName.trim(),
      direction: this.createModel.direction,
      kind: this.createModel.kind,
      providerKey: this.createModel.providerKey.trim(),
      authConfigRef: this.createModel.authConfigRef?.trim() || null,
      mappingJson: mergeConnectorMappingJson(this.createModel.mappingJson),
      webhookSecret: this.createModel.webhookSecret.trim() || null,
      status: this.createModel.status,
      scheduleMode: this.createModel.scheduleMode
    }).pipe(finalize(() => this.savingCreate.set(false))).subscribe({
      next: () => {
        this.createDialogVisible.set(false);
        this.load();
        void this.router.navigate(['/growth/integration/connectors', key]);
      },
      error: () => this.errorMessage.set(this.t('growth.integration.connectors.messages.saveFailed'))
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthIntegrationTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
